/* LO-CAB collection scrolling for Impulse 9.2.0. Standard pagination remains the fallback. */
(() => {
  'use strict';
  function releasePendingPagination() {
    clearTimeout(window.locabScrollPendingTimer);
    document.documentElement.classList.remove('locab-scroll-pending');
  }
  function clearRestoration() {
    clearTimeout(window.locabRestoreSlowTimer);
    clearTimeout(window.locabRestoreSafetyTimer);
    document.documentElement.classList.remove('locab-collection-restoring', 'locab-collection-restoring-slow');
    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    releasePendingPagination();
  }
  if (!document.body.classList.contains('template-collection') || !('IntersectionObserver' in window)) { clearRestoration(); return; }

  const root = document.querySelector('.collection-content[data-section-type="collection-grid"]');
  if (!root) { clearRestoration(); return; }
  const storagePrefix = 'locab:collection-scroll:';
  const lifetime = 2 * 60 * 60 * 1000;
  let instance;
  if ('caches' in window) {
    caches.keys().then(names => Promise.all(names.filter(name => name.startsWith('locab92-collection-pages-')).map(async name => {
      const cache = await caches.open(name);
      const requests = await cache.keys();
      await Promise.all(requests.map(async request => {
        const response = await cache.match(request);
        const at = Number(response?.headers.get('x-locab-cached-at') || 0);
        if (!at || Date.now() - at > 3600000) await cache.delete(request);
      }));
    }))).catch(() => {});
  }

  function key() {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    return storagePrefix + url.pathname + url.search;
  }
  function saved() {
    try {
      const state = JSON.parse(sessionStorage.getItem(key()) || 'null');
      return state && Date.now() - state.at < lifetime ? state : null;
    } catch (_) { return null; }
  }
  function isBackNavigation() {
    const entry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    return entry && entry.type === 'back_forward';
  }
  function waitForImages() {
    return new Promise(resolve => {
      const images = Array.from(root.querySelectorAll('.collection-grid__wrapper .grid--uniform img'));
      Promise.race([
        Promise.all(images.map(img => img.complete ? Promise.resolve() : new Promise(done => {
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }))),
        new Promise(done => setTimeout(done, 1800))
      ]).then(() => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    });
  }

  class Scroller {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector('.collection-grid__wrapper .grid--uniform');
      this.source = section.querySelector('[data-locab-next-url]');
      this.pager = section.querySelector('.pagination');
      this.controllers = new Set();
      this.count = 1;
      this.buffer = null;
      this.prefetch = null;
      this.busy = false;
      this.disposed = false;
      this.cacheName = 'locab92-collection-pages-' + [window.Shopify?.country, window.Shopify?.currency?.active, document.documentElement.lang].map(value => (value || 'default').toLowerCase().replace(/[^a-z0-9-]/g, '-')).join('-');
      this.next = this.nextLink();
      this.status = document.createElement('div');
      this.status.className = 'locab-continuous-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.innerHTML = '<span class="locab-continuous-message"></span><button type="button" class="btn btn--tertiary locab-continuous-retry" hidden>Retry loading products</button>';
      this.section.appendChild(this.status);
      this.message = this.status.querySelector('.locab-continuous-message');
      this.retry = this.status.querySelector('button');
      this.retry.addEventListener('click', () => { this.retry.hidden = true; this.load(); });
      this.grid.addEventListener('click', event => {
        const link = event.target.closest('a[href*="/products/"]');
        if (!link || !this.grid.contains(link)) return;
        try {
          sessionStorage.setItem(key(), JSON.stringify({ pages: this.count, y: scrollY, at: Date.now() }));
          const products = Array.from(this.grid.querySelectorAll('.grid-product[data-product-handle]'), tile => ({ handle: tile.dataset.productHandle, type: tile.dataset.productType || '' }));
          sessionStorage.setItem('locab:collection-trail', JSON.stringify({ collection: location.pathname.replace(/\/$/, ''), products, at: Date.now() }));
        } catch (_) {}
      });
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.load();
      }, { rootMargin: '1500px 0px' });
    }
    nextLink() {
      const path = this.source?.dataset.locabNextUrl;
      return path ? new URL(path, location.origin).href : '';
    }
    dispose() {
      this.disposed = true;
      this.observer.disconnect();
      this.controllers.forEach(controller => controller.abort());
      this.controllers.clear();
      this.status.remove();
      this.section.classList.remove('locab-continuous-active');
      this.pager?.classList.remove('locab-continuous-pagination');
      clearRestoration();
    }
    async start() {
      if (!this.grid || !this.pager || !this.next) {
        this.section.classList.remove('locab-continuous-active');
        this.status.remove();
        clearRestoration();
        return;
      }
      this.section.classList.add('locab-continuous-active');
      releasePendingPagination();
      this.pager.classList.add('locab-continuous-pagination');
      const state = isBackNavigation() ? saved() : null;
      if (state && state.pages > 1) {
        this.message.textContent = 'Restoring products…';
        for (let i = 1; i < state.pages && this.next && !this.disposed; i++) {
          try { this.append(await this.fetchPage(this.next, true)); }
          catch (_) { break; }
        }
      }
      if (state && !this.disposed) {
        await waitForImages();
        window.scrollTo(0, state.y);
      }
      clearRestoration();
      if (this.disposed) return;
      this.message.textContent = '';
      if (this.next) {
        this.prefetchNext();
        this.observer.observe(this.status);
      } else this.finish();
    }
    async cachedHtml(url) {
      if (!('caches' in window)) return null;
      try {
        const cache = await caches.open(this.cacheName);
        const response = await cache.match(url);
        if (!response) return null;
        const at = Number(response.headers.get('x-locab-cached-at') || 0);
        if (!at || Date.now() - at > 3600000) { await cache.delete(url); return null; }
        return response.text();
      } catch (_) { return null; }
    }
    async cacheHtml(url, html) {
      if (!('caches' in window)) return;
      try {
        const cache = await caches.open(this.cacheName);
        await cache.put(url, new Response(html, { headers: { 'content-type': 'text/html', 'x-locab-cached-at': String(Date.now()) } }));
      } catch (_) { /* Network fetching remains available. */ }
    }
    parsePage(url, html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.collection-content[data-section-type="collection-grid"]');
      const grid = section?.querySelector('.collection-grid__wrapper .grid--uniform');
      if (!grid || !grid.querySelector('.grid-product')) throw new Error('Collection products missing');
      return { url, grid, nextUrl: section.querySelector('[data-locab-next-url]')?.dataset.locabNextUrl || '', pager: section.querySelector('.pagination'), modals: section.querySelectorAll('.modal--quick-shop') };
    }
    async fetchPage(url, preferCache = false) {
      if (preferCache) {
        const html = await this.cachedHtml(url);
        if (html) return this.parsePage(url, html);
      }
      const controller = new AbortController();
      this.controllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const html = await response.text();
        this.cacheHtml(url, html);
        return this.parsePage(url, html);
      } finally { clearTimeout(timeout); this.controllers.delete(controller); }
    }
    prefetchNext() {
      if (!this.next || this.prefetch || this.buffer || this.disposed) return;
      const url = this.next;
      this.prefetch = this.fetchPage(url).then(page => {
        if (!this.disposed && this.next === url) this.buffer = page;
        return page;
      }).catch(() => null).finally(() => { this.prefetch = null; });
    }
    append(page) {
      if (this.disposed || this.next !== page.url) return;
      const existing = new Set(Array.from(this.grid.querySelectorAll('.grid-product[data-product-handle]'), el => el.dataset.productHandle));
      const fragment = document.createDocumentFragment();
      page.grid.querySelectorAll(':scope > .grid-product').forEach(item => {
        const handle = item.dataset.productHandle;
        if (handle && existing.has(handle)) return;
        existing.add(handle);
        fragment.appendChild(document.importNode(item, true));
      });
      this.grid.appendChild(fragment);
      if (page.pager) {
        this.pager.replaceWith(document.importNode(page.pager, true));
        this.pager = this.section.querySelector('.pagination');
        this.pager.classList.add('locab-continuous-pagination');
      } else {
        this.pager.remove();
        this.pager = null;
      }
      this.next = page.nextUrl ? new URL(page.nextUrl, location.origin).href : '';
      this.count++;
      try {
        if (window.theme?.reinitProductGridItem) window.theme.reinitProductGridItem(this.section);
      } catch (error) { console.warn('[LO-CAB scroll] Product tile enhancement failed.', error); }
      document.dispatchEvent(new CustomEvent('locab:collection-page-loaded', { detail: { url: page.url, grid: this.grid } }));
    }
    async load() {
      if (this.busy || !this.next || this.disposed) return;
      this.busy = true;
      this.retry.hidden = true;
      this.message.textContent = 'Loading more products…';
      try {
        if (this.prefetch) await this.prefetch;
        const page = this.buffer || await this.fetchPage(this.next);
        this.buffer = null;
        this.append(page);
        this.message.textContent = '';
        if (this.next) this.prefetchNext();
        else this.finish();
      } catch (error) {
        if (!this.disposed) {
          this.message.textContent = 'Products could not load automatically.';
          this.retry.hidden = false;
          this.section.classList.remove('locab-continuous-active');
          this.pager?.classList.remove('locab-continuous-pagination');
        }
      } finally {
        this.busy = false;
        if (this.next && this.retry.hidden && !this.disposed && this.status.getBoundingClientRect().top < innerHeight + 1500) requestAnimationFrame(() => this.load());
      }
    }
    finish() {
      this.observer.disconnect();
      this.status.remove();
    }
  }

  function init() {
    const section = root;
    const grid = section.querySelector('.collection-grid__wrapper .grid--uniform');
    if (!grid || grid === instance?.grid) return;
    instance?.dispose();
    instance = new Scroller(section);
    instance.start();
  }
  // Impulse replaces #CollectionAjaxContent after sorting and filtering.
  document.addEventListener('collection:reloaded', init);
  new MutationObserver(() => init()).observe(root.querySelector('#CollectionAjaxContent') || root, { childList: true });
  init();
  window.addEventListener('pageshow', event => {
    if (event.persisted && saved()) window.scrollTo(0, saved().y);
  });
})();
